// Shared constant values for the Stuck Refund Workflow dashboard.
import type { Region, StuckRow } from './types';

export const REGIONS: Region[] = ['NA', 'EU', 'FE'];

/** Map the app's realm (from the env selector) to our region code. */
export const REALM_TO_REGION_CODE: Record<string, Region> = {
    NAAmazon: 'NA',
    'eu-south-2': 'EU',
    FEAmazon: 'FE',
};

/* ---------- Facet definitions ---------- */
export const STUCK_FACETS: Array<[string, string, keyof StuckRow]> = [
    ['region', 'Region', 'region'],
    ['rail', 'Rail', 'rail'],
    ['workflow', 'Workflow', 'workflow'],
    ['plugin', 'Plugin', 'plugin'],
    ['op', 'Operation', 'op'],
];

export const REFUND_OPS = ['Refund', 'OverRefund', 'Disburse'];

/** Orca workflow labels (mirror backend StuckWorkflowDataGenerator.WORKFLOWS). */
export const WORKFLOW = { PCES: 'PCES-Retry', NATIVE: 'PMRAS-ProcessPMR' } as const;
/** Refund rail values. */
export const RAIL = { PCES: 'PCES', NATIVE: 'Native' } as const;

/* ---------- Chart / layout ---------- */
export const REGION_COLORS: Record<string, string> = { NA: '#4a9eff', EU: '#43c59e', FE: '#e0a458' };

/**
 * A plugin/rail group with more than this many stuck work items is treated as a SAM ticket
 * candidate and shown expanded; smaller groups collapse into "Others". 40 matches the standalone
 * stuck_workflow_dashboard's SAM threshold — tune here if SAM's per-segment paging changes.
 */
export const STUCK_TICKET_THRESHOLD = 40;

/** SAM keys on EXACT op-segment states: ClassifyError::Error::<plugin>:<Op> (no suffix). */
export const EXACT_OP = /ClassifyError::Error::[^:]+:(Refund|OverRefund|Cancel|Initiate|Finalize|Disburse)$/;

export const ORCA_HOSTS: Record<string, string> = {
    NA: 'us-east-1.studio.orca.amazon.dev',
    EU: 'eu-south-2.studio.orca.amazon.dev',
    FE: 'us-west-2.studio.orca.amazon.dev',
};

/**
 * Orca client/workflow/index identifiers, kept in ONE place so the deep-links stay in sync.
 * These MUST mirror the backend StuckWorkflowDataGenerator.WORKFLOWS table (SherlockWebappService);
 * if a client id or workflow name changes there, update it here too.
 */
export const ORCA = {
    PCES: { client: 'payment-contract-dev', workflow: 'PaymentExecutionRetry', index: 'WorkItemsByState' },
    NATIVE: { client: 'paystation-refunds', workflow: 'ProcessPaymentMethodRefund', index: 'WorkItemsByStateByPlugin' },
} as const;

/* ---------- Plugin labels ---------- */
// The backend emits a real plugin name or the single sentinel "(no plugin)" for a missing one.
export const UNRESOLVED_PLUGINS = new Set(['(no plugin)']);
export const PLATFORM_PLUGIN = 'Platform';
export const PLATFORM_PLUGIN_NATIVE = 'Platform (Native)';
// Plugins excluded from per-plugin SAM ticketing: the unresolved sentinel + the synthetic Platform labels.
export const INVALID_PLUGINS = new Set([...Array.from(UNRESOLVED_PLUGINS), PLATFORM_PLUGIN, PLATFORM_PLUGIN_NATIVE]);
